/**
 * @namespace source.items
 */
import { toItem } from './mappers/item';
import { decodeItemId } from './parsers/item-id';
import { toItem as toPrivateItem } from '../private-site/mappers/item';
import { toError } from '../private/parsers/error';
import { extendDeep, isSameSite } from '../../utils/utils';
import { MediaType } from '../../constants/MediaType';
import { WIX_VIDEO_LIBRARY_LINK } from '../wix-video/data';
import { systemFolderId } from '../../constants/system-folder-id';
export function initItems(settings, http, privateSiteItems, wixVideoItems) {
  const defaultPaging = {
    size: 50,
    cursor: null
  };
  const defaultSort = {
    order: 'date',
    direction: 'desc'
  };
  function fetchOldItems(url, folderId, options, overridingQueryParams) {
    if (options === void 0) {
      options = {};
    }
    if (overridingQueryParams === void 0) {
      overridingQueryParams = {};
    }
    const sort = {
      ...defaultSort,
      ...options.sort
    };
    const paging = {
      ...defaultPaging,
      ...options.paging
    };
    const queryParams = {
      page_size: paging.size,
      cursor: paging.cursor,
      media_type: options.mediaType || MediaType.Picture
    };
    queryParams.order = sort.direction === 'desc' ? `-${sort.order}` : sort.order;
    if (folderId) {
      queryParams.parent_folder_id = decodeItemId(folderId).id;
    }
    http.setRequestOptionsOnce({
      params: {
        site_token: null
      }
    });
    return http.get(url, extendDeep(queryParams, overridingQueryParams), {
      headers: options.headers || {}
    }).then(function (response) {
      response.paging = {
        size: paging.size,
        cursor: response.data.cursor
      };
      return {
        ...response,
        data: (response.data.files || []).map(item => toItem(toPrivateItem(item, settings, options.thumbnails), folderId))
      };
    }).catch(toError);
  }
  function listOldItems(folderId, options) {
    if (options === void 0) {
      options = {};
    }
    return fetchOldItems(`${settings.apiUrl}/media/files/list`, folderId, options);
  }
  function searchOldItems(folderId, query, options) {
    if (options === void 0) {
      options = {};
    }
    return fetchOldItems(`${settings.apiUrl}/media/files/search`, folderId, options, {
      query
    });
  }
  function list(folderId, options) {
    if (!folderId || folderId === 'wix-sites') {
      return Promise.resolve({
        data: []
      });
    }
    const {
      id,
      siteMediaToken
    } = decodeItemId(folderId);
    if (!siteMediaToken) {
      return listOldItems(folderId, options);
    }

    // hide contents of current site, no need to import to itself
    const topLevelSiteToken = settings.topLevelMediaToken || settings.siteMediaToken;
    const sameSites = isSameSite(topLevelSiteToken, siteMediaToken) && id !== systemFolderId.Purchased;
    return privateSiteItems.list(id, {
      ...options,
      siteMediaToken
    }).then(response => ({
      ...response,
      data: [...(sameSites ? [] : response.data.map(item => toItem(item, folderId)))]
    }));
  }
  function search(folderId, query, options) {
    if (!folderId || folderId === 'wix-sites') {
      return Promise.resolve({
        data: []
      });
    }
    const {
      id,
      siteMediaToken
    } = decodeItemId(folderId);
    if (!siteMediaToken) {
      return searchOldItems(folderId, query, options);
    }
    if (id === WIX_VIDEO_LIBRARY_LINK) {
      return wixVideoItems.search(id, query, {
        ...options,
        siteMediaToken
      });
    }
    return privateSiteItems.search(id, query, {
      ...options,
      siteMediaToken
    }).then(response => ({
      ...response,
      data: response.data.map(item => toItem(item, folderId))
    }));
  }
  function importToSite(item, destination, options) {
    if (destination === void 0) {
      destination = {};
    }
    if (options === void 0) {
      options = {};
    }
    const decodedItemId = decodeItemId(item.id);
    return privateSiteItems.copy([{
      ...item,
      id: decodedItemId.id
    }], destination, {
      ...options,
      isFromG6: Boolean(decodedItemId.siteMediaToken)
    });
  }
  return {
    list,
    search,
    importToSite
  };
}